// ==========================================================================
// components/TasksWidget.tsx — the family to-do list on Home (FF-033)
// Type, pick who it's for, optionally a due date, Enter. Tap the circle to
// tick off. Done items fade and drop off after three days.
// ==========================================================================
import { useState, type FormEvent } from "react";
import { format, parseISO, isBefore } from "date-fns";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Plus, ShoppingBasket } from "lucide-react";
import TaskSheet from "./TaskSheet";
import { useShopping } from "@/lib/kitchen";
import { usePeople } from "@/lib/household";
import { tokenColour, ymd } from "@/lib/calendar";
import { useAddTask, useTasks, useToggleTask, type Task } from "@/lib/tasks";

export default function TasksWidget() {
  const tasks = useTasks(); const people = usePeople(); const add = useAddTask(); const toggle = useToggleTask();
  const adults = (people.data ?? []).filter((p) => p.role === "adult");
  const [title, setTitle] = useState(""); const [who, setWho] = useState<string | null>(null); const [due, setDue] = useState("");
  const [filter, setFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  const shopping = useShopping(false);
  const toGet = (shopping.data ?? []).filter((i) => !i.checked_at).length;
  const today = ymd(new Date());

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await add.mutateAsync({ title: title.trim(), assignee_id: who, due_on: due || null });
      setTitle(""); setDue("");
    } catch { /* Mutation error is shown below; keep the draft for retry. */ }
  }
  const list = (tasks.data ?? []).filter((t) => !filter || t.assignee_id === filter || t.assignee_id === null);
  const open = (tasks.data ?? []).filter((t) => !t.done_at).length;

  return (
    <section className="rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
      <div className="flex items-center justify-between px-5 pb-2 pt-5">
        <h2 className="text-[17px] font-semibold">To do <span className="font-normal text-ink-muted">· {open}</span></h2>
        <div className="flex gap-1">
          {[{ id: null, name: "All", colour: "text-muted" }, ...adults.map((p) => ({ id: p.id, name: p.display_name, colour: p.colour }))].map((p) => (
            <button key={p.name} type="button" aria-pressed={filter === p.id} onClick={() => setFilter(p.id)}
              className={`rounded-full px-2.5 py-1 text-xs ${filter === p.id ? "bg-primary font-semibold text-primary-ink" : "text-ink-muted hover:text-ink"}`}>{p.name}</button>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="mx-5 mb-3 rounded-md bg-surface-raised p-2">
        <div className="flex items-center gap-2">
          <Plus className="ml-1 h-4 w-4 shrink-0 text-ink-muted" />
          <input aria-label="New task" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task and press Enter" className="min-w-0 flex-1 bg-transparent py-1.5 text-[15px] outline-none placeholder:text-ink-muted" />
        </div>
        {title && (
          <div className="mt-2 flex flex-wrap items-center gap-2 pl-7">
            <span className="text-xs text-ink-muted">For</span>
            {[{ id: null as string | null, display_name: "Anyone", colour: "text-muted" }, ...adults].map((p) => (
              <button key={p.display_name} type="button" aria-pressed={who === p.id} onClick={() => setWho(p.id)}
                className={`rounded-full px-2.5 py-0.5 text-xs ${who === p.id ? "bg-primary font-semibold text-primary-ink" : "bg-surface text-ink ring-1 ring-line"}`}>{p.display_name}</button>
            ))}
            <input aria-label="Task due date" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="rounded-md bg-surface px-2 py-0.5 text-xs ring-1 ring-line" />
          </div>
        )}
      </form>
      {(add.error || toggle.error) && <p role="alert" className="px-5 pb-3 text-sm text-bad">Couldn't save the task. {add.error?.message ?? toggle.error?.message}</p>}

      <ul className="max-h-[360px] overflow-y-auto px-2 pb-3">
        {list.length === 0 && <li className="px-3 py-4 text-sm text-ink-muted">Nothing to do. Nice.</li>}
        {list.map((t) => {
          const p = people.data?.find((x) => x.id === t.assignee_id);
          const late = !t.done_at && t.due_on && isBefore(parseISO(t.due_on), parseISO(today));
          return (
            <li key={t.id} className={`flex items-start gap-3 rounded-md px-3 py-2.5 hover:bg-surface-raised/60 ${t.done_at ? "opacity-50" : ""}`}>
              <button type="button" aria-label={t.done_at ? "Mark not done" : "Mark done"} onClick={() => toggle.mutate(t)}
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition"
                style={{ borderColor: tokenColour(p?.colour ?? "text-muted"), background: t.done_at ? tokenColour(p?.colour ?? "text-muted") : "transparent" }}>
                {t.done_at && <Check className="h-3 w-3 text-white" />}
              </button>
              <button type="button" onClick={() => setEditing(t)} className="min-w-0 flex-1 text-left">
                <span className={`flex items-center gap-1.5 text-[15px] ${t.done_at ? "line-through" : ""}`}>{t.kind === "shopping" && <ShoppingBasket className="h-4 w-4 shrink-0 text-primary" />}{t.title}</span>
                <span className="flex gap-2 text-xs text-ink-muted">
                  <span>{p?.display_name ?? "Anyone"}</span>
                  {t.due_on && <span className={late ? "font-semibold text-bad" : ""}>{t.due_on === today ? "today" : format(parseISO(t.due_on), "EEE d MMM")}</span>}
                  {t.kind === "shopping" && !t.done_at && <span>{toGet} to get</span>}
                  {t.note && <span className="truncate">{t.note}</span>}
                </span>
              </button>
              {t.kind === "shopping" && !t.done_at && (
                <Link to="/kitchen/list" aria-label="Open the shopping list" className="flex shrink-0 items-center gap-0.5 self-center rounded-full bg-primary/10 py-1 pl-2.5 pr-1.5 text-xs font-semibold text-primary">List<ChevronRight className="h-3.5 w-3.5" /></Link>
              )}
            </li>
          );
        })}
      </ul>
      {editing && <TaskSheet task={editing} listCount={editing.kind === "shopping" ? toGet : undefined} onClose={() => setEditing(null)} />}
    </section>
  );
}
